import React, { useState, useEffect } from "react";
import {
  Loader2,
  ExternalLink,
  Info,
  AlertTriangle,
  Building2,
  User,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import {
  orphanService,
  Orphan,
  OrphanType,
} from "../../services/OrphanService";
import { revSyncService } from "../../services/RevSyncService";
import { useToastContext } from "../../context/ToastContext";

interface OrphansProps {
  embedded?: boolean;
  onCountsChange?: (count: number) => void;
}

const Orphans: React.FC<OrphansProps> = ({
  embedded = false,
  onCountsChange,
}) => {
  const { showToast } = useToastContext();
  const [orphans, setOrphans] = useState<Orphan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadOrphans();
  }, []);

  const loadOrphans = async () => {
    setLoading(true);
    try {
      const response = await orphanService.getOrphans();
      setOrphans(response.data.orphans);
    } catch (error: any) {
      console.error("Error loading orphans:", error);
      showToast("error", error.message || "Failed to load orphans");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const { message, userMessage } =
        await revSyncService.triggerReconciliation();
      showToast("success", userMessage || message || "Reconciliation started.");
      await loadOrphans();
    } catch (error: any) {
      console.error("Error triggering reconciliation:", error);
      showToast("error", error.message || "Failed to start reconciliation.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const allowedOrphanTypes: OrphanType[] = [
    "deal_without_company",
    "deal_without_contact",
    "deal_without_line_items",
    "deal_without_customer_identity",
  ] as OrphanType[];

  const getOrphanActionText = (type: OrphanType) => {
    switch (type) {
      case "deal_without_line_items":
        return "Add line items to the deal, then re-run reconciliation.";
      case "deal_without_customer_identity":
        return "Add a contact email or company domain, then re-run reconciliation.";
      default:
        return "Fix the missing company/contact in HubSpot, then re-run reconciliation.";
    }
  };

  const filteredOrphans = orphans.filter((orphan) =>
    allowedOrphanTypes.includes(orphan.type),
  );

  useEffect(() => {
    onCountsChange?.(filteredOrphans.length);
  }, [filteredOrphans.length, onCountsChange]);

  const containerClasses = embedded
    ? "w-full"
    : "w-full min-h-screen bg-background-default dark:bg-background-dark";
  const innerClasses = embedded ? "w-full" : "max-w-7xl mx-auto px-6 py-6";
  const titleClasses = embedded
    ? "text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-1"
    : "text-2xl font-semibold text-text-primary dark:text-text-primaryDark mb-1 tracking-tight";
  const subtitleClasses = embedded
    ? "text-sm text-text-secondary dark:text-text-secondaryDark"
    : "text-sm text-text-secondary dark:text-text-secondaryDark";

  return (
    <div className={containerClasses}>
      <div className={innerClasses}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h1 className={titleClasses}>Orphans</h1>
            <p className={subtitleClasses}>
              Deals missing company and/or contact information
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-3 py-1.5 text-xs font-medium text-secondary hover:text-secondary-hover rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 bg-transparent hover:bg-secondary/5"
          >
            {isRefreshing ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </>
            )}
          </button>
        </div>

        {/* Summary Card */}
        {!loading && (
          <div className="mb-4 bg-background-paper dark:bg-background-paperDark rounded-2xl border border-divider/60 dark:border-divider-dark/60 p-4 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-background-panel dark:bg-background-panelDark rounded-xl">
                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <div className="text-xl font-semibold text-text-primary dark:text-text-primaryDark">
                  {filteredOrphans.length}
                </div>
                <div className="text-sm text-text-secondary dark:text-text-secondaryDark">
                  {filteredOrphans.length === 1
                    ? "Orphan deal"
                    : "Orphan deals"}{" "}
                  requiring attention
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orphans List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-secondary" />
          </div>
        ) : filteredOrphans.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-green-100 dark:bg-green-900/20 rounded-full mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
              No orphan deals found
            </h3>
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark text-center max-w-md">
              All your HubSpot deals have proper company and contact
              associations. Great job maintaining data integrity!
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {filteredOrphans.map((orphan) => (
              <div
                key={orphan._id}
                className="rounded-2xl border border-divider/60 dark:border-divider-dark/60 bg-background-paper dark:bg-background-paperDark px-6 py-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark truncate">
                        {orphan.metadata.dealName || "Unnamed Deal"}
                      </h3>
                      <span className="px-2.5 py-0.5 rounded-md text-xs font-medium border border-divider/60 dark:border-divider-dark/60 text-text-secondary dark:text-text-secondaryDark">
                        Orphan
                      </span>
                      {orphan.type === "deal_without_customer_identity" && (
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-medium border border-orange-300/70 dark:border-orange-700/40 text-orange-700 dark:text-orange-300 bg-orange-50/70 dark:bg-orange-900/20">
                          Missing Identity
                        </span>
                      )}
                      {orphan.type === "deal_without_line_items" && (
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-medium border border-orange-300/70 dark:border-orange-700/40 text-orange-700 dark:text-orange-300 bg-orange-50/70 dark:bg-orange-900/20">
                          Missing Line Items
                        </span>
                      )}
                      {(orphan.metadata.missingCompany ||
                        orphan.metadata.missingContact) && (
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-medium border border-orange-300/70 dark:border-orange-700/40 text-orange-700 dark:text-orange-300 bg-orange-50/70 dark:bg-orange-900/20">
                          Missing{" "}
                          {[
                            orphan.metadata.missingCompany ? "Company" : null,
                            orphan.metadata.missingContact ? "Contact" : null,
                          ]
                            .filter(Boolean)
                            .join(" + ")}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-text-secondary dark:text-text-secondaryDark">
                      {orphan.hubspotDealId && (
                        <span className="font-mono text-[11px]">
                          Deal ID: {orphan.hubspotDealId}
                        </span>
                      )}
                    </div>
                  </div>
                  {orphan.metadata.hubspotDealUrl && (
                    <a
                      href={orphan.metadata.hubspotDealUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary hover:text-secondary-hover transition-colors"
                    >
                      <span>
                        Open in <strong>HubSpot</strong>
                      </span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>

                <div className="mt-2 text-xs text-text-secondary dark:text-text-secondaryDark">
                  {getOrphanActionText(orphan.type)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Card */}
        <div className="mt-6 bg-background-panel dark:bg-background-panelDark rounded-2xl p-4 border border-divider/60 dark:border-divider-dark/60">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-background-default dark:bg-background-dark rounded-lg flex-shrink-0">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-1">
                About Orphans
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                Orphan deals are HubSpot deals that are missing required data
                for safe subscription linking such as a company or contact with
                an email or required line items. Update the deal in HubSpot and
                re-run reconciliation to bring it back into approvals.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orphans;
